// src/components/DistribucionesScreen.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import {
    db,
    collection,
    getDocs,
    orderBy,
    query,
    where,
    limit as fbLimit,
    doc,
    getDoc,
    updateDoc,
    documentId,
} from "../firebase";


const FUNCTIONS_BASE =
    "https://assistantfarmaciaspreview-ieitjwau4a-uc.a.run.app";

const FUNCTIONS_PREVIEW =
    "https://assistantfarmaciaspreview-ieitjwau4a-uc.a.run.app";

/* ========================= Helpers y Constantes ========================= */

// Sucursales default si no las pasás por props
const DEFAULT_SUCURSALES = {
    "1": { nombre: "Moscovich" },
    "2": { nombre: "Guberman" },
    "3": { nombre: "Sunchales" },
};

// Límite de concurrencia para lecturas de stock en lote
// Ajustá estos valores arriba de tu archivo
const STOCK_IN_CHUNK = 10;           // Firestore IN soporta máx 10
const VENTAS_IN_CHUNK = 10;          // idem
const STOCK_CONCURRENCY = 15;        // bajar agresividad
const SAFE_DAY_LIMIT = 400;          // tope de docs por día en fallback
const USE_VENTAS30D_AGG_FIRST = true; // priorizar colección agregada


function chunked(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}


// ---------- helpers visuales y de fecha ----------
function toInputDate(d) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}
function addDays(d, n) {
    const dd = new Date(d);
    dd.setDate(dd.getDate() + n);
    return dd;
}
function yyyymmddToHuman(s) {
    if (!s || typeof s !== "string" || s.length !== 8) return s || "";
    const yyyy = s.slice(0, 4);
    const mm = s.slice(4, 6);
    const dd = s.slice(6, 8);
    return `${dd}/${mm}/${yyyy}`;
}
function toYmd(d) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}${mm}${dd}`;
}
function getSkuFromKey(productKey) {
    if (!productKey) return "";
    if (productKey.startsWith("SKU:")) return productKey.slice(4);
    return "";
}
function escapeHtml(str) {
    return String(str || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}
function fmtInt(n) {
    const x = Number(n);
    return Number.isFinite(x) ? String(Math.round(x)) : "—";
}
function fmtDays(n) {
    if (!Number.isFinite(n)) return "∞";
    if (n < 10) return (Math.round(n * 10) / 10).toFixed(1);
    return String(Math.round(n));
}

// ---------- Query de Firestore para la lista ----------
function buildQuery({ dateFrom, dateTo, ownerId }) {
    const coll = collection(db, "distribuciones");
    const fromStr = (dateFrom || "").replaceAll("-", "");
    const toStr = (dateTo || "").replaceAll("-", "");

    const clauses = [];
    if (fromStr) clauses.push(where("period.end", ">=", fromStr));
    if (toStr) clauses.push(where("period.end", "<=", toStr));

    const raw = (ownerId ?? "").toString().trim();
    if (raw && raw !== "all") {
        if (/^\d+$/.test(raw)) clauses.push(where("owner.id", "==", raw));
        else clauses.push(where("owner.nombre", "==", raw));
    }
    return query(coll, ...clauses, orderBy("period.end", "desc"), fbLimit(20));
}

function ventas30dFromIndex(idx, sucursalId, sku) {
    return Number(idx?.[`${String(sucursalId)}|${String(sku)}`] ?? 0) || 0;
}

// ---------- Índice de ventas 30d (1 sola query) ----------
async function preloadVentas30dIndexSmart({ items, sucList }) {
    // 1) Intentá agregados (rápido)
    if (USE_VENTAS30D_AGG_FIRST) {
        try {
            const agg = await tryLoadVentas30dAggregated({ sucList, items });
            if (agg) return agg;
        } catch (e) {
            console.warn("[ventas30d] agregados no disponibles, voy al fallback", e);
        }
    }

    // 2) Fallback por día (30 queries pequeñas)
    const today = new Date();
    const start = addDays(today, -30);
    const startYmd = toYmd(start);
    const endYmd = toYmd(today);
    const skus = skuSetFromItems(items);
    return await fallbackVentasPorDia({ startYmd, endYmd, skuWhitelist: skus });
}


function skuSetFromItems(items = []) {
    return new Set(items.map((it) => getSkuFromKey(it.productKey)).filter(Boolean));
}

// ---------- Stock en lote (paralelo + caché local por doc) ----------

async function buildStockIndex({ sucList, items }) {
    const skuList = Array.from(
        new Set(items.map(it => getSkuFromKey(it.productKey)).filter(Boolean))
    );
    if (!skuList.length) return {};

    const idx = Object.create(null);

    // Por sucursal, pedimos en chunks con documentId() IN (máx 10)
    for (const sid of sucList) {
        const skChunks = chunked(skuList, STOCK_IN_CHUNK);
        for (const group of skChunks) {
            const coll = collection(db, "stocks", String(sid), "skus");
            const qy = query(coll, where(documentId(), "in", group));
            const snap = await getDocs(qy);
            for (const d of snap.docs) {
                const n = Number(d.data()?.stock) || 0;
                idx[`${sid}|${d.id}`] = n;
            }
            // los que no vinieron en el snap => 0
            for (const sku of group) {
                const key = `${sid}|${sku}`;
                if (!(key in idx)) idx[key] = 0;
            }
        }
    }
    return idx;
}

async function tryLoadVentas30dAggregated({ sucList, items }) {
    const skuList = Array.from(
        new Set(items.map(it => getSkuFromKey(it.productKey)).filter(Boolean))
    );
    if (!skuList.length) return null;

    const idx = Object.create(null);

    for (const sid of sucList) {
        const skChunks = chunked(skuList, VENTAS_IN_CHUNK);
        for (const group of skChunks) {
            const coll = collection(db, "ventas30d", String(sid), "skus");
            const qy = query(coll, where(documentId(), "in", group));
            const snap = await getDocs(qy);
            for (const d of snap.docs) {
                const qty30d = Number(d.data()?.qty30d) || 0;
                idx[`${sid}|${d.id}`] = qty30d;
            }
            // faltantes = 0
            for (const sku of group) {
                const key = `${sid}|${sku}`;
                if (!(key in idx)) idx[key] = 0;
            }
        }
    }
    return idx; // mapa "suc|sku" -> total 30d
}

async function fallbackVentasPorDia({ startYmd, endYmd, skuWhitelist }) {
    // Generamos lista de YYYYMMDD entre start y end (inclusive)
    const days = [];
    {
        const y = Number(startYmd.slice(0, 4));
        const m = Number(startYmd.slice(4, 6)) - 1;
        const d = Number(startYmd.slice(6, 8));
        const start = new Date(y, m, d);
        const y2 = Number(endYmd.slice(0, 4));
        const m2 = Number(endYmd.slice(4, 6)) - 1;
        const d2 = Number(endYmd.slice(6, 8));
        const end = new Date(y2, m2, d2);
        for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
            const mm = String(dt.getMonth() + 1).padStart(2, "0");
            const dd = String(dt.getDate()).padStart(2, "0");
            days.push(`${dt.getFullYear()}${mm}${dd}`);
        }
    }

    const idx = Object.create(null);

    for (const ymd of days) {
        const coll = collection(db, "ventas");
        // un query pequeño por día + limit para no explotar
        const qy = query(coll, where("fecha", "==", ymd), fbLimit(SAFE_DAY_LIMIT));
        const snap = await getDocs(qy);

        for (const docSnap of snap.docs) {
            const data = docSnap.data() || {};
            const productos = Array.isArray(data.productos) ? data.productos : [];
            for (const p of productos) {
                const sku = String(p?.sku ?? "").trim();
                if (!sku || (skuWhitelist && !skuWhitelist.has(sku))) continue;
                const suc = String(p?.sucursal ?? "").trim();
                if (!suc) continue;
                const n = Number(p?.cantidad ?? 0);
                if (!Number.isFinite(n) || n <= 0) continue;
                idx[`${suc}|${sku}`] = (idx[`${suc}|${sku}`] || 0) + n;
            }
        }
        // (opcional) si querés, logueá si se capó el día:
        // if (snap.size === SAFE_DAY_LIMIT) console.warn("Cap reached on", ymd);
    }

    return idx;
}


// ---------- Algoritmo por cobertura (ventas 30d) ----------
function computeCoverageSplit(qty, stockActual, ventas30d) {
    const sucIds = Object.keys(stockActual || {});
    const alloc = {};
    for (const sid of sucIds) alloc[sid] = 0;

    const state = sucIds.map((sid) => {
        const stock = Number(stockActual[sid] ?? 0) || 0;
        const sold30 = Number(ventas30d[sid] ?? 0) || 0;
        const daily = sold30 / 30;
        const coverageDays = daily > 0 ? stock / daily : Infinity;
        return { sid, stock, daily, coverageDays };
    });

    function recalcCoverage(i) {
        const x = state[i];
        x.coverageDays = x.daily > 0 ? x.stock / x.daily : Infinity;
    }

    let remaining = Number(qty) || 0;
    while (remaining > 0 && state.length > 0) {
        let best = 0;
        for (let i = 1; i < state.length; i++) {
            if (state[i].coverageDays < state[best].coverageDays) best = i;
        }
        state[best].stock += 1;
        alloc[state[best].sid] += 1;
        recalcCoverage(best);
        remaining -= 1;
    }
    return alloc;
}

// ---------- Construcción HTML para imprimir/exportar ----------
function buildInnerPrintableHtml({ detailDoc, sucursales, items, overrideDistMap }) {
    const sucList = Object.keys(sucursales);
    const sucHeader = sucList
        .map((sid) => `<th class="th">${sucursales[sid]?.nombre || `Suc ${sid}`}</th>`)
        .join("");

    const itemsHtml = items
        .map((it) => {
            const sku = getSkuFromKey(it.productKey) || "—";
            const dist = overrideDistMap?.[it.productKey] || it.distPorSuc || {};
            const distCells = sucList
                .map((sid) => `<td class="td right">${dist?.[sid] ?? 0}</td>`)
                .join("");

            return `
        <tr>
          <td class="td nowrap">${sku}</td>
          <td class="td">${escapeHtml(it.descripcionDisplay || it.descripcion || "")}</td>
          <td class="td right">${it.aReponer ?? 0}</td>
          ${distCells}
        </tr>`;
        })
        .join("");

    return `
    <div id="pdf-root">
      <style>
        #pdf-root, #pdf-root * {
          color: #000 !important; background: transparent !important;
          background-image: none !important; box-shadow: none !important;
          text-shadow: none !important; filter: none !important;
          outline-color: #000 !important; border-color: #ddd !important;
        }
        #pdf-root { font-family: Arial, Helvetica, sans-serif !important;
          line-height: 1.3 !important; font-size: 12px !important;
          padding: 0 !important; margin: 0 !important; color-scheme: light !important; }
        .title { font-size: 16px; font-weight: bold; margin-bottom: 4px; }
        .meta { margin-bottom: 12px; font-size: 12px; color: #444 !important; }
        table { border-collapse: collapse; width: 100%; }
        .th { background: #f7f7f7 !important; border: 1px solid #ddd !important; padding: 6px !important; text-align: left !important; }
        .td { border: 1px solid #ddd !important; padding: 6px !important; }
        .right { text-align: right !important; }
        .nowrap { white-space: nowrap !important; }
        tr, td, th { page-break-inside: avoid !important; }
      </style>

      <div class="title">${escapeHtml(detailDoc.laboratorio?.display || detailDoc.laboratorio?.norm || "Laboratorio")}</div>
      <div class="meta">
        Pedido desde: ${escapeHtml(detailDoc.owner?.nombre || `Suc ${detailDoc.owner?.id || "?"}`)}<br/>
        Período: ${yyyymmddToHuman(detailDoc.period?.start)} → ${yyyymmddToHuman(detailDoc.period?.end)}<br/>
        Total ítems: ${detailDoc.items?.length ?? 0} · Total unidades: ${detailDoc.resumen?.totalUnidades ?? 0}
      </div>

      <table>
        <thead>
          <tr>
            <th class="th">SKU</th>
            <th class="th">Descripción</th>
            <th class="th">Cantidad pedida</th>
            ${sucHeader}
          </tr>
        </thead>
        <tbody>${itemsHtml}</tbody>
      </table>
    </div>
  `;
}

function buildHtmlForDoc({ detailDoc, sucursales, items, overrideDistMap }) {
    const sucList = Object.keys(sucursales);

    const itemsHtml = items
        .map((it) => {
            const sku = getSkuFromKey(it.productKey) || "—";
            const dist = overrideDistMap?.[it.productKey] || it.distPorSuc || {};
            const distCells = sucList
                .map((sid) => `<td style="border:1px solid #ddd;padding:6px;text-align:right;">${dist?.[sid] ?? 0}</td>`)
                .join("");
            return `
        <tr>
          <td style="border:1px solid #ddd;padding:6px;white-space:nowrap;">${sku}</td>
          <td style="border:1px solid #ddd;padding:6px;">${escapeHtml(it.descripcionDisplay || it.descripcion || "")}</td>
          <td style="border:1px solid #ddd;padding:6px;text-align:right;">${it.aReponer ?? 0}</td>
          ${distCells}
        </tr>`;
        })
        .join("");

    const sucHeader = sucList
        .map((sid) => `<th style="border:1px solid #ddd;padding:6px;">${sucursales[sid]?.nombre || `Suc ${sid}`}</th>`)
        .join("");

    return `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Distribución - ${escapeHtml(detailDoc.laboratorio?.display || detailDoc.laboratorio?.norm || "")}</title>
        <style>
          body { font-family: Arial, sans-serif; }
          .meta { margin-bottom: 12px; font-size: 12px; color: #444; }
          table { border-collapse: collapse; width: 100%; }
          th { background: #f7f7f7; border:1px solid #ddd; padding: 6px; text-align: left; }
          td { border:1px solid #ddd; padding:6px; }
          .title { font-size: 16px; font-weight: bold; margin-bottom: 4px; }
        </style>
      </head>
      <body>
        ${buildInnerPrintableHtml({ detailDoc, sucursales, items, overrideDistMap })}
      </body>
    </html>
  `;
}

function buildPdfFileName(detailDoc) {
    const lab = (detailDoc.laboratorio?.display || detailDoc.laboratorio?.norm || "LAB").replace(/\s+/g, "_");
    const owner = (detailDoc.owner?.nombre || `Suc_${detailDoc.owner?.id || "?"}`).replace(/\s+/g, "_");
    const period = `${detailDoc.period?.start || ""}-${detailDoc.period?.end || ""}`;
    return `Distribucion_${lab}_${owner}_${period}.pdf`;
}


/** Convierte el bloque 'owner' del preview a un pseudo-doc como los de 'distribuciones' */
function buildDetailDocFromPreview(ownerBlock, period) {
    const totalesPorSuc = {};
    let totalUnidades = 0;

    for (const it of ownerBlock.productos || []) {
        totalUnidades += Number(it.aReponer || 0);
        const dist = it.distPorSuc || {};
        for (const [sid, n] of Object.entries(dist)) {
            totalesPorSuc[sid] = (totalesPorSuc[sid] || 0) + Number(n || 0);
        }
    }

    return {
        id: `PREVIEW_${ownerBlock.ownerSucId}_${ownerBlock.labNorm}_${period?.end || ""}`,
        createdAt: Date.now(),
        period: { start: period?.start || "", end: period?.end || "" },
        owner: ownerBlock.owner,
        laboratorio: { norm: ownerBlock.labNorm, display: ownerBlock.labDisplay },
        resumen: {
            totalUnidades,
            porSucursal: Object.fromEntries(
                Object.entries(totalesPorSuc).map(([sid, val]) => [
                    sid === "1" ? "Moscovich" : sid === "2" ? "Guberman" : sid === "3" ? "Sunchales" : `Suc ${sid}`,
                    val,
                ])
            ),
        },
        // Items con mismo shape que usás en la tabla del modal
        items: (ownerBlock.productos || []).map((p) => ({
            productKey: p.productKey,
            sku: getSkuFromKey(p.productKey),
            descripcion: p.descripcion,
            descripcionDisplay: p.descripcion, // viene limpio
            aReponer: Number(p.aReponer || 0),
            distPorSuc: p.distPorSuc || {},
            laboratorio: p.laboratorio,
            labDisplay: p.labDisplay,
        })),
        entregado: false,
    };
}





/* ========================= Componente principal ========================= */

export default function DistribucionesScreen({ sucursales = DEFAULT_SUCURSALES }) {
    const [rowsRaw, setRowsRaw] = useState([]);
    const [loading, setLoading] = useState(false);

    const today = useMemo(() => new Date(), []);
    const [dateFrom, setDateFrom] = useState(() => toInputDate(addDays(today, -30)));
    const [dateTo, setDateTo] = useState(() => toInputDate(today));
    const [ownerId, setOwnerId] = useState("all");
    const [labQuery, setLabQuery] = useState("");

    // Modal
    const [open, setOpen] = useState(false);
    const [detailDoc, setDetailDoc] = useState(null);

    // Recalculo (vista/memoria)
    const [recalcLoading, setRecalcLoading] = useState(false);
    const [recalcDistMap, setRecalcDistMap] = useState({});
    const [metricsMap, setMetricsMap] = useState({});
    const [ventasIdx, setVentasIdx] = useState(null);
    const [recalcProgress, setRecalcProgress] = useState({ step: "", done: 0, total: 0 });

    // Guardados
    const [savingEntregado, setSavingEntregado] = useState(false);
    const [savingRecalc, setSavingRecalc] = useState(false);

    // PDF/Print
    const printableRef = useRef(null);

    // evitar carreras en fetch
    const lastFetchId = useRef(0);

    /* ------ Fetch tabla principal al cambiar filtros ------ */
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFrom, dateTo, ownerId]);

    async function fetchData() {
        const fetchId = ++lastFetchId.current;
        try {
            setLoading(true);
            const q = buildQuery({ dateFrom, dateTo, ownerId });
            const snap = await getDocs(q);
            if (fetchId !== lastFetchId.current) return;
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setRowsRaw(list);
        } catch (err) {
            console.error("[DistribucionesScreen] fetchData error", err);
            setRowsRaw([]);
        } finally {
            if (fetchId === lastFetchId.current) setLoading(false);
        }
    }

    async function recalcWithPreviewLab() {
        const labName =
            (detailDoc?.laboratorio?.display || detailDoc?.laboratorio?.norm || labQuery || "").toString().trim();
        if (!labName) {
            console.warn("[recalcWithPreviewLab] laboratorio vacío");
            return;
        }

        setRecalcLoading(true);
        setRecalcProgress({ step: "Calculando preview en servidor…", done: 0, total: 0 });

        try {
            const resp = await fetch(FUNCTIONS_PREVIEW, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ labs: [labName] }),
            });

            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err?.error || `HTTP ${resp.status}`);
            }

            const data = await resp.json();
            if (!data?.ok) throw new Error(data?.error || "preview-failed");

            const ownerIdWanted = String(detailDoc?.owner?.id || "");
            let ownerBlock = null;
            if (Array.isArray(data.owners) && data.owners.length) {
                ownerBlock = data.owners.find((o) => String(o.ownerSucId) === ownerIdWanted) || data.owners[0];
            }
            if (!ownerBlock) {
                setRecalcDistMap({});
                setMetricsMap({});
                return;
            }

            const synthetic = buildDetailDocFromPreview(ownerBlock, data.period);
            // setDetailDoc(synthetic);

            // Set de productKeys del doc original (para no agregar filas nuevas)
            const allowed = new Set((detailDoc?.items || []).map((it) => it.productKey));

            // Cargamos SOLO la distribución recalculada de ítems que ya existen
            const map = {};
            for (const it of ownerBlock.productos || []) {
                if (allowed.has(it.productKey)) {
                    map[it.productKey] = it.distPorSuc || {};
                }
            }

            // Guardamos SOLO la nueva distribución en memoria
            setRecalcDistMap(map);
            setMetricsMap({});
        } catch (e) {
            console.error("[recalcWithPreviewLab] error", e);
        } finally {
            setRecalcLoading(false);
            setRecalcProgress({ step: "", done: 0, total: 0 });
        }
    }



    /* ------ Filtro en memoria por laboratorio ------ */
    const rows = useMemo(() => {
        if (!labQuery.trim()) return rowsRaw;
        const qtxt = labQuery.trim().toLowerCase();
        return rowsRaw.filter((x) => {
            const labText = `${x.laboratorio?.display || ""} ${x.laboratorio?.norm || ""}`.toLowerCase();
            return labText.includes(qtxt);
        });
    }, [rowsRaw, labQuery]);

    /* ------ Abrir modal de detalle ------ */
    const openDetail = (docRow) => {
        setDetailDoc(docRow);

        // pre-popular con recálculo guardado si existe
        const pre = {};
        if (docRow?.distRecalc?.items && Array.isArray(docRow.distRecalc.items)) {
            const allowed = new Set((docRow.items || []).map((it) => it.productKey));
            for (const it of docRow.distRecalc.items) {
                if (!it?.productKey) continue;
                if (!allowed.has(it.productKey)) continue; // ⛔️ ignora filas que no existan en el doc original
                pre[it.productKey] = it.newDistPorSuc || {};
            }
        }
        setRecalcDistMap(pre);

        setMetricsMap({});
        setVentasIdx(null); // reseteamos índice por doc abierto
        setRecalcProgress({ step: "", done: 0, total: 0 });
        setOpen(true);
    };

    /* ------ Orden de items en el modal ------ */
    const sortedItems = useMemo(() => {
        if (!detailDoc?.items) return [];
        return [...detailDoc.items].sort((a, b) =>
            (a.descripcionDisplay || a.descripcion || "").localeCompare(
                b.descripcionDisplay || b.descripcion || "",
                "es",
                { sensitivity: "base", numeric: true }
            )
        );
    }, [detailDoc]);

    /* ------ Recalcular rápido (ventas 30d + stock bulk) ------ */
    async function recalcWithBackend() {
        if (!detailDoc?.id) return;
        setRecalcLoading(true);
        setRecalcProgress({ step: "Recalculando en servidor…", done: 0, total: 0 });

        try {
            const url = `${FUNCTIONS_BASE}/distribucionRecalcHttp`;
            const resp = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    docId: detailDoc.id,
                    // opcional: enviar las sucursales del front por si querés que el backend las use
                    // sucursales,
                }),
            });

            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err?.error || `HTTP ${resp.status}`);
            }

            const data = await resp.json();
            if (!data?.ok) throw new Error(data?.error || "recalc-failed");

            // Mapas para UI (misma estructura que usabas antes)
            setRecalcDistMap(data.recalcDistMap || {});
            setMetricsMap(data.metricsMap || {});
        } catch (e) {
            console.error("[recalcWithBackend] error", e);
            // opcional: toast
            // toast.error(`Error al recalcular: ${e.message}`);
        } finally {
            setRecalcLoading(false);
            setRecalcProgress({ step: "", done: 0, total: 0 });
        }
    }



    async function recalcAndSave() {
        await recalcWithPreviewLab();
        await persistRecalc(detailDoc, recalcDistMap);
    }

    /* ------ Guardados ------ */
    async function markAsDelivered(docRow) {
        if (!docRow?.id) return;
        try {
            setSavingEntregado(true);
            const ref = doc(db, "distribuciones", docRow.id);
            const ts = Date.now();
            await updateDoc(ref, { entregado: true, entregadoAt: ts });

            setRowsRaw((prev) => prev.map((r) => (r.id === docRow.id ? { ...r, entregado: true, entregadoAt: ts } : r)));
            setDetailDoc((d) => (d && d.id === docRow.id ? { ...d, entregado: true, entregadoAt: ts } : d));
        } catch (err) {
            console.error("[markAsDelivered] error", err);
        } finally {
            setSavingEntregado(false);
        }
    }

    async function persistRecalc(detailDocLocal, recalcMapLocal) {
        if (!detailDocLocal?.id || !detailDocLocal?.items?.length) return;
        try {
            setSavingRecalc(true);
            const ts = Date.now();
            const payloadItems = detailDocLocal.items.map((it) => ({
                productKey: it.productKey,
                baseQty: Number(it.aReponer ?? 0) || 0,
                newDistPorSuc: recalcMapLocal[it.productKey] || it.distPorSuc || {},
                sku: getSkuFromKey(it.productKey) || "",
                descripcion: it.descripcionDisplay || it.descripcion || "",
            }));
            const ref = doc(db, "distribuciones", detailDocLocal.id);
            await updateDoc(ref, { distRecalc: { recalculatedAt: ts, items: payloadItems } });

            setRowsRaw((prev) =>
                prev.map((r) => (r.id === detailDocLocal.id ? { ...r, distRecalc: { recalculatedAt: ts, items: payloadItems } } : r))
            );
            setDetailDoc((d) =>
                d && d.id === detailDocLocal.id ? { ...d, distRecalc: { recalculatedAt: ts, items: payloadItems } } : d
            );
        } catch (err) {
            console.error("[persistRecalc] error", err);
        } finally {
            setSavingRecalc(false);
        }
    }

    /* ------ Flags/UI ------ */
    const canSaveRecalc = useMemo(() => {
        if (!detailDoc?.items?.length) return false;
        for (const it of detailDoc.items) {
            const orig = it.distPorSuc || {};
            const neu = recalcDistMap[it.productKey];
            if (!neu) continue;
            const sucIds = new Set([...Object.keys(orig), ...Object.keys(neu)]);
            for (const sid of sucIds) {
                const a = Number(orig[sid] ?? 0);
                const b = Number(neu[sid] ?? 0);
                if (a !== b) return true;
            }
        }
        if (!detailDoc?.distRecalc?.items?.length) {
            const keys = Object.keys(recalcDistMap || {});
            if (keys.length > 0) return true;
        }
        return false;
    }, [detailDoc, recalcDistMap]);

    const renderedRows = useMemo(
        () =>
            rows.map((docRow) => (
                <tr key={docRow.id} className="align-top">
                    <td className="whitespace-nowrap">
                        {docRow.period?.end
                            ? yyyymmddToHuman(docRow.period.end)
                            : docRow.createdAt
                                ? new Date(docRow.createdAt).toLocaleString()
                                : ""}
                    </td>
                    <td>{docRow.owner?.nombre || `Suc ${docRow.owner?.id || "?"}`}</td>
                    <td>{docRow.laboratorio?.display || docRow.laboratorio?.norm || "—"}</td>
                    <td>{docRow.items?.length ?? 0}</td>
                    <td>{docRow.resumen?.totalUnidades ?? 0}</td>
                    <td><ChipsPorSucursal map={docRow.resumen?.porSucursal} /></td>
                    <td className="whitespace-nowrap">
                        {docRow.entregado ? (
                            <span className="badge badge-success gap-1 text-xs">✅ Entregado</span>
                        ) : (
                            <span className="badge badge-warning gap-1 text-xs">⏳ Pendiente</span>
                        )}
                    </td>
                    <td className="whitespace-nowrap flex flex-col gap-1">
                        <button className="btn btn-outline btn-xs" onClick={() => openDetail(docRow)}>👁️ Ver</button>
                        {!docRow.entregado && (
                            <button className="btn btn-outline btn-xs" disabled={savingEntregado} onClick={() => markAsDelivered(docRow)}>
                                {savingEntregado ? "..." : "✅ Marcar entregado"}
                            </button>
                        )}
                        <button
                            className="btn btn-outline btn-xs"
                            disabled={recalcLoading}
                            onClick={async () => {
                                openDetail(docRow);
                                await recalcWithBackend();
                            }}
                        >
                            ♻️ Recalcular
                        </button>
                    </td>
                </tr>
            )),
        [rows, savingEntregado, recalcLoading]
    );

    const sucList = Object.keys(sucursales);

    // ¿hay recálculo en memoria o guardado?
    const hasAnyRecalc = useMemo(() => {
        const hasSaved = Array.isArray(detailDoc?.distRecalc?.items) && detailDoc.distRecalc.items.length > 0;
        const hasLive = recalcDistMap && Object.keys(recalcDistMap).length > 0;
        return hasSaved || hasLive;
    }, [detailDoc, recalcDistMap]);

    // Devuelve la distribución "Recalc" para un item (live > guardado > null)
    function getRecalcDistForItem(it) {
        if (recalcDistMap?.[it.productKey]) return recalcDistMap[it.productKey];
        const saved = detailDoc?.distRecalc?.items?.find(x => x.productKey === it.productKey);
        return saved?.newDistPorSuc || null;
    }

    // util para delta (opcional)
    const fmtDelta = (a, b) => {
        const d = b - a;
        if (!Number.isFinite(d) || d === 0) return "";
        return (d > 0 ? `+${d}` : `${d}`);
    };



    /* ------ Print / Download PDF ------ */
    const printDetail = () => {
        if (!detailDoc) return;
        const html = buildHtmlForDoc({
            detailDoc,
            sucursales,
            items: sortedItems,
            overrideDistMap: recalcDistMap,
        });
        const w = window.open("", "_blank", "width=1100,height=800");
        if (!w) return;
        w.document.open();
        w.document.write(html);
        w.document.close();
        w.onload = () => w.print();
    };

    async function downloadPdf() {
        if (!detailDoc) return;

        const inner = buildInnerPrintableHtml({
            detailDoc,
            sucursales,
            items: sortedItems,
            overrideDistMap: recalcDistMap,
        });

        // Iframe aislado para que html2pdf no herede estilos raros
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.left = "-99999px";
        iframe.style.top = "0";
        iframe.style.width = "210mm";
        iframe.style.height = "297mm";
        iframe.setAttribute("sandbox", "allow-same-origin");
        document.body.appendChild(iframe);

        const idoc = iframe.contentDocument || iframe.contentWindow.document;
        idoc.open();
        idoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="color-scheme" content="light" />
          <title>PDF</title>
          <style>
            html, body { margin: 0; padding: 0; background: #ffffff !important; color: #000 !important; }
            :root, *, *::before, *::after {
              background-image: none !important; box-shadow: none !important; filter: none !important;
            }
          </style>
        </head>
        <body>${inner}</body>
      </html>
    `);
        idoc.close();

        const root = idoc.querySelector("#pdf-root");
        if (!root) {
            iframe.remove();
            console.error("[downloadPdf] #pdf-root no encontrado");
            return;
        }

        const fileName = buildPdfFileName(detailDoc);
        const opt = {
            margin: 10,
            filename: fileName,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
                windowWidth: 794,   // ~A4 @96dpi
                windowHeight: 1123, // ~A4 @96dpi
                onclone: (clonedDoc) => {
                    const bad = (s) =>
                        s && (s.includes("color(") || s.includes("oklch(") || s.includes("lab(") || s.includes("lch(") || s.includes("color-mix("));
                    clonedDoc.querySelectorAll("*").forEach((el) => {
                        const cs = clonedDoc.defaultView.getComputedStyle(el);
                        if (bad(cs.backgroundColor)) el.style.backgroundColor = "#ffffff";
                        if (bad(cs.color)) el.style.color = "#000000";
                        const bgImg = cs.backgroundImage || "";
                        if (bad(bgImg)) el.style.backgroundImage = "none";
                    });
                },
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        };

        try {
            await html2pdf().set(opt).from(root).save();
        } finally {
            iframe.remove();
        }
    }

    /* ------ Render ------ */
    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">Pedidos para distribuir</h1>

            {/* Filtros */}
            <div className="bg-base-100 p-4 rounded shadow mb-6">
                <h2 className="text-xl font-semibold mb-3">Filtros</h2>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="flex flex-col">
                        <label className="text-sm mb-1">Desde (por fecha de fin)</label>
                        <input type="date" className="input input-bordered" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-sm mb-1">Hasta (por fecha de fin)</label>
                        <input type="date" className="input input-bordered" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-sm mb-1">Pedido desde</label>
                        <select className="select select-bordered" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                            <option value="all">Todas</option>
                            {Object.entries(sucursales).map(([id, meta]) => (
                                <option key={id} value={String(id)}>{meta?.nombre || `Suc ${id}`}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col md:col-span-2">
                        <label className="text-sm mb-1">Laboratorio (texto)</label>
                        <input className="input input-bordered" placeholder="p. ej. GADOR" value={labQuery} onChange={(e) => setLabQuery(e.target.value)} />
                    </div>
                </div>
                <div className="mt-4">
                    <button className="btn btn-outline" onClick={fetchData} disabled={loading}>
                        {loading ? (<><span className="loading loading-spinner loading-sm mr-2" />Cargando…</>) : "Actualizar"}
                    </button>
                </div>
            </div>

            {/* Resultados */}
            <div className="bg-base-100 p-4 rounded shadow">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-semibold">Resultados ({rows.length})</h3>
                </div>
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <span className="loading loading-spinner loading-lg" />
                    </div>
                ) : (
                    <div className="w-full overflow-auto">
                        <table className="table table-zebra w-full text-sm">
                            <thead>
                                <tr className="text-left">
                                    <th>Fecha</th>
                                    <th>Pedido desde</th>
                                    <th>Laboratorio</th>
                                    <th>Ítems</th>
                                    <th>Total</th>
                                    <th>Por sucursal</th>
                                    <th className="whitespace-nowrap">Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {renderedRows}
                                {!rows.length && !loading && (
                                    <tr>
                                        <td className="py-6 text-center opacity-70" colSpan={8}>Sin resultados en el período.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal detalle */}
            {open && detailDoc && (
                <div className="modal modal-open" onClick={() => setOpen(false)}>
                    <div className="modal-box max-w-6xl w-11/12 relative" onClick={(e) => e.stopPropagation()}>
                        {/* Overlay mientras recalcula */}
                        {recalcLoading && <LoaderOverlay text="Recalculando distribución…" />}
                        <h3 className="font-bold text-lg mb-1 flex items-center gap-2 flex-wrap">
                            <span>{detailDoc.laboratorio?.display || detailDoc.laboratorio?.norm} · {detailDoc.owner?.nombre}</span>
                            {detailDoc.entregado
                                ? <span className="badge badge-success text-xs">✅ Entregado</span>
                                : <span className="badge badge-warning text-xs">⏳ Pendiente</span>}
                        </h3>
                        <p className="text-xs opacity-70 mb-3">
                            Período: {yyyymmddToHuman(detailDoc.period?.start)} → {yyyymmddToHuman(detailDoc.period?.end)}<br />
                            {detailDoc.entregadoAt ? `Entregado el ${new Date(detailDoc.entregadoAt).toLocaleString()}` : ""}
                        </p>

                        <div className="text-xs mb-3">
                            {detailDoc.distRecalc?.recalculatedAt ? (
                                <div className="flex flex-col gap-1">
                                    <div className="badge badge-info badge-outline w-fit text-[10px]">
                                        Último recálculo guardado: {new Date(detailDoc.distRecalc.recalculatedAt).toLocaleString()}
                                    </div>
                                    <div className="opacity-70">Mostrando "Recalc" guardado. Podés recalcular si cambió el stock.</div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    <div className="badge badge-neutral badge-outline w-fit text-[10px]">Sin recálculo guardado todavía</div>
                                    <div className="opacity-70">Tocá "♻️ Recalcular ahora" para proponer nueva distribución.</div>
                                </div>
                            )}
                        </div>

                        {/* Aviso si hay recálculo en memoria sin guardar */}
                        {canSaveRecalc && (
                            <div className="text-[11px] text-warning font-medium mb-3 flex items-center gap-2">
                                <span className="badge badge-warning badge-xs" />
                                <span>Tenés una propuesta nueva sin guardar. Si es la final, tocá "💾 Guardar recálculo".</span>
                            </div>
                        )}

                        {/* Progreso recálculo */}
                        {recalcLoading && (
                            <div className="mb-3 text-xs flex items-center gap-2">
                                <span className="loading loading-spinner loading-sm" />
                                <span>Recalculando en el servidor…</span>
                            </div>
                        )}


                        <div ref={printableRef} className="max-h-[60vh] overflow-auto">
                            <table className="table w-full text-sm">
                                <thead>
                                    <tr className="text-left align-bottom">
                                        <th rowSpan={hasAnyRecalc ? 2 : 1}>SKU</th>
                                        <th rowSpan={hasAnyRecalc ? 2 : 1}>Descripción</th>
                                        <th rowSpan={hasAnyRecalc ? 2 : 1}>Cantidad pedida</th>
                                        {sucList.map((sid) => (
                                            <th key={`h-${sid}`} colSpan={hasAnyRecalc ? 2 : 1} className="text-center whitespace-nowrap">
                                                {sucursales[sid]?.nombre || `Suc ${sid}`}
                                            </th>
                                        ))}
                                    </tr>
                                    {hasAnyRecalc && (
                                        <tr className="text-left text-xs opacity-70">
                                            {sucList.map((sid) => (
                                                <React.Fragment key={`sub-${sid}`}>
                                                    <th className="whitespace-nowrap font-normal">Orig</th>
                                                    <th className="whitespace-nowrap font-normal">Recalc</th>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    )}
                                </thead>


                                <tbody>
                                    {sortedItems.map((it) => {
                                        const origDist = it.distPorSuc || {};
                                        const recalcDist = getRecalcDistForItem(it); // puede ser null

                                        return (
                                            <tr key={it.productKey}>
                                                <td className="whitespace-nowrap">{getSkuFromKey(it.productKey) || "—"}</td>
                                                <td>{it.descripcionDisplay || it.descripcion}</td>
                                                <td className="text-right">{it.aReponer ?? 0}</td>

                                                {sucList.map((sid) => {
                                                    const origVal = Number(origDist?.[sid] ?? 0);
                                                    const hasRecalcValue = recalcDist && Object.prototype.hasOwnProperty.call(recalcDist, sid);
                                                    const recVal = hasRecalcValue ? Number(recalcDist?.[sid] ?? 0) : null;
                                                    const changed = hasRecalcValue && recVal !== origVal;

                                                    return hasAnyRecalc ? (
                                                        <React.Fragment key={`${it.productKey}-${sid}`}>
                                                            {/* ORIG: siempre visible, intacto */}
                                                            <td className="text-right text-xs align-top">{fmtInt(origVal)}</td>

                                                            {/* RECALC: solo si cambió; si no, celda vacía y desaturada */}
                                                            <td
                                                                className={
                                                                    "text-right text-xs align-top " +
                                                                    (changed
                                                                        ? "font-semibold bg-yellow-100 dark:bg-yellow-900/30 rounded"
                                                                        : "opacity-40")
                                                                }
                                                                title={changed ? `Δ ${fmtDelta(origVal, recVal)}` : "Sin cambios"}
                                                            >
                                                                {changed ? (
                                                                    <>
                                                                        {fmtInt(recVal)}
                                                                        {/* opcional: mini delta */}
                                                                        <span className="ml-1 text-[10px] opacity-70">({fmtDelta(origVal, recVal)})</span>
                                                                    </>
                                                                ) : (
                                                                    "" // oculto si no cambió
                                                                )}
                                                            </td>
                                                        </React.Fragment>
                                                    ) : (
                                                        // Sin recálculo: 1 sola columna por sucursal
                                                        <td key={`${it.productKey}-${sid}`} className="text-right text-xs align-top">
                                                            {fmtInt(origVal)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>


                            </table>
                            <div className="mt-2 text-[11px] opacity-70">
                                {hasAnyRecalc ? (
                                    <>En “Recalc” solo se muestran celdas que difieren del original (se ocultan los iguales).</>
                                ) : (
                                    <>Mostrando la distribución original.</>
                                )}
                            </div>
                        </div>

                        <div className="modal-action flex flex-wrap gap-2">
                            {!detailDoc.entregado && (
                                <button className="btn btn-success" disabled={savingEntregado} onClick={() => markAsDelivered(detailDoc)}>
                                    {savingEntregado ? "..." : "✅ Marcar entregado"}
                                </button>
                            )}
                            <button
                                className="btn btn-outline"
                                disabled={recalcLoading}
                                onClick={recalcWithPreviewLab}
                                title="Recalcular usando stock actual y ventas 30d (solo vista)"
                            >
                                {recalcLoading ? "..." : "♻️ Recalcular ahora"}
                            </button>
                            <button
                                className={`btn btn-outline ${!canSaveRecalc ? "btn-disabled opacity-40" : ""}`}
                                disabled={recalcLoading || savingRecalc || !canSaveRecalc}
                                onClick={recalcAndSave}
                                title={canSaveRecalc ? "Guardar recálculo en Firestore" : "Primero ejecutá ♻️ Recalcular ahora"}
                            >
                                {recalcLoading || savingRecalc ? "..." : "💾 Guardar recálculo"}
                            </button>
                            <button className="btn btn-outline" onClick={printDetail}>🖨️ Imprimir PDF</button>
                            <button className="btn btn-outline" onClick={downloadPdf}>⬇️ Descargar PDF</button>
                            <button className="btn" onClick={() => setOpen(false)}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* --------------------- Subcomponentes --------------------- */
function ChipsPorSucursal({ map }) {
    const entries = Object.entries(map || {});
    if (!entries.length) return <span className="opacity-60">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {entries.map(([name, total]) => (
                <span key={name} className="badge badge-ghost gap-1" title={`${name}: ${total}`}>
                    {name}: {total}
                </span>
            ))}
        </div>
    );
}


function LoaderOverlay({ text = "Calculando…" }) {
    return (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 backdrop-blur-sm">
            <div className="flex items-center gap-3 bg-base-100 px-4 py-3 rounded shadow">
                <span className="loading loading-spinner loading-md" />
                <span className="text-sm font-medium">{text}</span>
            </div>
        </div>
    );
}